import React, { useState } from "react";

export const FraudAnalysisPane = ({ analysisData }) => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    metadata: false,
    fonts: false,
    content: false,
    structure: false,
    technical: false,
  });

  if (!analysisData) {
    return (
      <div className="p-4 text-center text-gray-500">
        No fraud analysis data available
      </div>
    );
  }

  const {
    fraud_indicators,
    metadata,
    fonts,
    content,
    structure,
    technical,
  } = analysisData;

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toUpperCase()) {
      case "HIGH":
        return "text-red-600 bg-red-100";
      case "MEDIUM":
        return "text-orange-600 bg-orange-100";
      case "LOW":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const parsePdfDate = (pdfDate) => {
    if (!pdfDate || !pdfDate.startsWith("D:")) return pdfDate;

    const raw = pdfDate.substring(2); // remove "D:"
    const datePart = raw.substring(0, 14); // YYYYMMDDHHmmSS
    const tzPart = raw.substring(14); // +06'00' or -05'30', etc.

    const year = datePart.substring(0, 4);
    const month = datePart.substring(4, 6);
    const day = datePart.substring(6, 8);
    const hour = datePart.substring(8, 10);
    const minute = datePart.substring(10, 12);
    const second = datePart.substring(12, 14);

    // Handle timezone, e.g. +06'00'
    let timezone = "Z"; // default UTC
    const match = tzPart.match(/([+-])(\d{2})'?(\d{2})'?/);
    if (match) {
      const sign = match[1];
      const hours = match[2];
      const minutes = match[3];
      timezone = `${sign}${hours}:${minutes}`;
    }

    // Format to ISO string
    const iso = `${year}-${month}-${day}T${hour}:${minute}:${second}${timezone}`;

    try {
      return new Date(iso).toLocaleString(); // human-readable local time
    } catch {
      return pdfDate; // fallback
    }
  };
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const SectionHeader = ({ title, isExpanded, onToggle, count }) => (
    <div
      className="flex justify-between items-center p-3 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-700">{title}</span>
        {count !== undefined && (
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
            {count}
          </span>
        )}
      </div>
      <span className="text-gray-400">{isExpanded ? "▼" : "▶"}</span>
    </div>
  );

  return (
    <div className="h-full bg-white border-l border-gray-200 overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-800">Fraud Analysis</h3>
        <p className="text-sm text-gray-600 mt-1">
          {analysisData.file_info?.filename}
        </p>
      </div>

      {/* Overview Section */}
      <div className="border-b">
        <SectionHeader
          title="Risk Overview"
          isExpanded={expandedSections.overview}
          onToggle={() => toggleSection("overview")}
        />
        {expandedSections.overview && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                Risk Level:
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(
                  fraud_indicators?.risk_level
                )}`}
              >
                {fraud_indicators?.risk_level || "UNKNOWN"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                Risk Score:
              </span>
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {(fraud_indicators?.risk_score * 100)?.toFixed(1) || 0}%
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                Total Flags:
              </span>
              <span className="text-sm font-medium text-gray-800">
                {fraud_indicators?.summary?.total_flags || 0}
              </span>
            </div>

            {fraud_indicators?.flags && fraud_indicators.flags.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Active Flags:
                </p>
                <div className="space-y-1">
                  {fraud_indicators.flags.map((flag, index) => (
                    <div
                      key={index}
                      className="text-xs p-2 bg-yellow-50 border-l-4 border-yellow-400"
                    >
                      <span className="font-medium capitalize">
                        {flag.type}:
                      </span>{" "}
                      {flag.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Metadata Section - Enhanced */}
      <div className="border-b">
        <SectionHeader
          title="Metadata Analysis"
          isExpanded={expandedSections.metadata}
          onToggle={() => toggleSection("metadata")}
          count={fraud_indicators?.categories?.metadata?.flags?.length}
        />
        {expandedSections.metadata && (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-gray-600">Author:</span>
                <p className="text-gray-800 mt-1">
                  {metadata?.author || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Creator:</span>
                <p className="text-gray-800 mt-1">
                  {metadata?.creator || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Producer:</span>
                <p className="text-gray-800 mt-1">
                  {metadata?.producer || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Created:</span>
                <p className="text-gray-800 mt-1">
                  {metadata?.creation_date?.formatted || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Modified:</span>
                <p className="text-gray-800 mt-1">
                  {metadata?.modification_date?.formatted || "Unknown"}
                </p>
              </div>
                <div></div>
              {/* Suspicious Software Analysis */}
              {metadata?.suspicious_software_analysis && (
                <div className="col-span-2 mt-3 p-2 bg-yellow-50 rounded">
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    Suspicious Software Analysis:
                  </p>

                  <div className="text-xs text-gray-800 space-y-1">
                    {metadata.suspicious_software_analysis.flags?.length >
                      0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          Flags:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                          {metadata.suspicious_software_analysis.flags.map(
                            (flag, i) => (
                              <li key={i}>{flag}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Date Analysis */}
            {metadata?.analysis?.date_analysis && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Date Analysis:
                </p>
                <div className="space-y-1 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <span>
                      Creation exists:{" "}
                      {metadata.analysis.date_analysis.creation_exists
                        ? "✓"
                        : "✗"}
                    </span>
                    <span>
                      Modification exists:{" "}
                      {metadata.analysis.date_analysis.modification_exists
                        ? "✓"
                        : "✗"}
                    </span>
                  </div>
                  {metadata.analysis.date_analysis.time_difference_seconds !==
                    null && (
                    <div className="text-gray-600">
                      Time difference:{" "}
                      {metadata.analysis.date_analysis.time_difference_seconds}s
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Analysis Flags */}
            {metadata?.analysis && (
              <div className="mt-3 p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Analysis:
                </p>
                <div className="space-y-1 text-xs">
                  {metadata.analysis.dates_both_missing === true && (
                    <div className="text-orange-600">
                      ⚠ Both creation and modification dates are missing
                    </div>
                  )}
                  {metadata.analysis.dates_match &&
                    metadata.analysis.dates_both_present && (
                      <div className="text-orange-600">
                        ⚠ Creation and modification dates are identical
                      </div>
                    )}
                  {metadata.analysis.suspicious_creator === true && (
                    <div className="text-red-600">
                      🚨 Suspicious creator software
                    </div>
                  )}
                  {metadata.analysis.generic_producer === true && (
                    <div className="text-yellow-600">
                      ⚠ Generic producer detected
                    </div>
                  )}
                  {metadata.analysis.missing_fields?.length > 0 && (
                    <div className="text-orange-600">
                      ⚠ Missing fields:{" "}
                      {metadata.analysis.missing_fields.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* XMP Metadata if available */}
            {metadata?.xmp_metadata && (
              <div className="mt-3 p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  XMP Metadata:
                </p>
                <div className="space-y-1 text-xs text-gray-700">
                  {Object.entries(metadata.xmp_metadata).map(([key, value]) => {
                    // Custom formatting for date fields
                    const isPdfDate =
                      typeof value === "string" && value.startsWith("D:");
                    const formattedValue = isPdfDate
                      ? parsePdfDate(value)
                      : String(value);

                    return (
                      <div key={key}>
                        <span className="font-semibold">{key}:</span>{" "}
                        {formattedValue}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Font Analysis Section */}
      <div className="border-b">
        <SectionHeader
          title="Font Analysis"
          isExpanded={expandedSections.fonts}
          onToggle={() => toggleSection("fonts")}
          count={fonts?.summary?.unique_fonts}
        />
        {expandedSections.fonts && (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-gray-600">Unique Fonts:</span>
                <p className="text-gray-800 mt-1">
                  {fonts?.summary?.unique_fonts || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Size Range:</span>
                <p className="text-gray-800 mt-1">
                  {fonts?.summary?.size_range?.min || 0}-
                  {fonts?.summary?.size_range?.max || 0}pt
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">
                  Dominant Font:
                </span>
                <p className="text-gray-800 mt-1">
                  {fonts?.summary?.dominant_font || "Unknown"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Dominance:</span>
                <p className="text-gray-800 mt-1">
                  {fonts?.summary?.dominant_percentage || 0}%
                </p>
              </div>
            </div>

            {/* Font Embedding Info */}
            {fonts?.font_embedding && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Font Embedding:
                </p>
                <div className="space-y-1 text-xs">
                  <div>
                    Total font objects:{" "}
                    {fonts.font_embedding.total_font_objects}
                  </div>
                  <div>
                    Embedded fonts: {fonts.font_embedding.embedded_count}
                  </div>
                </div>
              </div>
            )}

            {fonts?.font_list && fonts.font_list.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Font Details:
                </p>
                <div className="space-y-2">
                  {fonts.font_list.map((font, index) => (
                    <div key={index} className="text-xs p-2 bg-gray-50 rounded">
                      <div className="font-medium">{font.name}</div>
                      <div className="text-gray-600 mt-1">
                        {font.usage_percentage}% usage • Sizes:{" "}
                        {font.sizes_used.join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {fonts?.anomalies && (
              <div className="mt-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Font Anomalies:
                </p>
                <div className="space-y-1 text-xs">
                  {fonts.anomalies.too_many_fonts === true && (
                    <div className="text-orange-600">
                      ⚠ Too many fonts detected
                    </div>
                  )}
                  {fonts.anomalies.too_few_fonts === true && (
                    <div className="text-orange-600">
                      ⚠ Too few fonts detected
                    </div>
                  )}
                  {fonts.anomalies.high_variance === true && (
                    <div className="text-orange-600">
                      ⚠ High font size variance
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Analysis Section - Enhanced */}
      <div className="border-b">
        <SectionHeader
          title="Content Analysis"
          isExpanded={expandedSections.content}
          onToggle={() => toggleSection("content")}
          count={content?.text_stats?.word_count}
        />
        {expandedSections.content && (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-gray-600">Word Count:</span>
                <p className="text-gray-800 mt-1">
                  {content?.text_stats?.word_count || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Characters:</span>
                <p className="text-gray-800 mt-1">
                  {content?.text_stats?.total_length || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Sentences:</span>
                <p className="text-gray-800 mt-1">
                  {content?.text_stats?.sentence_count || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Lines:</span>
                <p className="text-gray-800 mt-1">
                  {content?.text_stats?.line_count || 0}
                </p>
              </div>
            </div>

            {/* Text Extraction Analysis */}
            {content?.extraction_info && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Text Extraction:
                </p>
                <div className="space-y-1 text-xs">
                  <div>Method used: {content.extraction_info.method_used}</div>
                  <div>
                    Fallback used:{" "}
                    {content.extraction_info.fallback_used ? "Yes" : "No"}
                  </div>
                  {content.extraction_info.obfuscated_analysis
                    ?.obfuscated_text_detected && (
                    <div className="text-red-600">
                      🚨 Obfuscated text detected
                    </div>
                  )}
                  {content.extraction_info.likely_issue && (
                    <div className="text-orange-600">
                      ⚠ {content.extraction_info.likely_issue}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Extraction Methods Comparison */}
            {content?.extraction_methods_comparison && (
              <div className="mt-3 p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Extraction Methods:
                </p>
                <div className="space-y-1 text-xs">
                  <div>
                    Text method:{" "}
                    {content.extraction_methods_comparison.text?.total_chars ||
                      0}{" "}
                    chars
                  </div>
                  <div>
                    HTML method:{" "}
                    {content.extraction_methods_comparison.html?.total_chars ||
                      0}{" "}
                    chars
                  </div>
                  <div>
                    Words method:{" "}
                    {content.extraction_methods_comparison.words?.total_words ||
                      0}{" "}
                    words
                  </div>
                  <div>
                    Dict method:{" "}
                    {content.extraction_methods_comparison.dict?.total_blocks ||
                      0}{" "}
                    blocks
                  </div>
                </div>
              </div>
            )}

            {content?.insurance_keywords && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Insurance Keywords:
                </p>
                <div className="space-y-1">
                  {Object.entries(content.insurance_keywords).map(
                    ([category, data]) => (
                      <div
                        key={category}
                        className="flex justify-between items-center text-xs"
                      >
                        <span className="capitalize text-gray-600">
                          {category.replace("_", " ")}:
                        </span>
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                          {data.count} ({(data.density * 100).toFixed(1)}%)
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {content?.detected_patterns && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Detected Patterns:
                </p>
                <div className="space-y-1 text-xs">
                  <div>
                    Policy Numbers:{" "}
                    {content.detected_patterns.policy_numbers?.length || 0}
                  </div>
                  <div>
                    Dates: {content.detected_patterns.dates?.length || 0}
                  </div>
                  <div>
                    Currency:{" "}
                    {content.detected_patterns.currency_amounts?.length || 0}
                  </div>
                  <div>
                    Addresses:{" "}
                    {content.detected_patterns.addresses?.length || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Structure Analysis Section */}
      <div className="border-b">
        <SectionHeader
          title="Structure Analysis"
          isExpanded={expandedSections.structure}
          onToggle={() => toggleSection("structure")}
          count={structure?.page_count}
        />
        {expandedSections.structure && (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-gray-600">Page Count:</span>
                <p className="text-gray-800 mt-1">
                  {structure?.page_count || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Text Blocks:</span>
                <p className="text-gray-800 mt-1">
                  {structure?.objects?.total_text_blocks || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Drawings:</span>
                <p className="text-gray-800 mt-1">
                  {structure?.objects?.total_drawings || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Images:</span>
                <p className="text-gray-800 mt-1">
                  {structure?.objects?.total_images || 0}
                </p>
              </div>
            </div>

            {/* Page Rotations */}
            {structure?.page_rotations && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Page Rotations:
                </p>
                <div className="space-y-1 text-xs">
                  <div>
                    Consistent rotation:{" "}
                    {structure.page_rotations.consistent_rotation
                      ? "Yes"
                      : "No"}
                  </div>
                  <div>
                    Rotation variance:{" "}
                    {structure.page_rotations.rotation_variance}
                  </div>
                </div>
              </div>
            )}

            {structure?.pages && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Page Details:
                </p>
                <div className="space-y-1">
                  {structure.pages.map((page, index) => (
                    <div key={index} className="text-xs p-2 bg-gray-50 rounded">
                      <div className="font-medium">Page {page.page}</div>
                      <div className="text-gray-600 mt-1 grid grid-cols-2 gap-2">
                        <span>Text: {page.objects.text_blocks}</span>
                        <span>Graphics: {page.objects.drawings}</span>
                        <span>Images: {page.objects.images}</span>
                        <span>Forms: {page.objects.form_fields}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Technical Analysis Section - Enhanced */}
      <div className="border-b">
        <SectionHeader
          title="Technical Analysis"
          isExpanded={expandedSections.technical}
          onToggle={() => toggleSection("technical")}
          count={technical?.table_detection?.tables_found}
        />
        {expandedSections.technical && (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium text-gray-600">Tables Found:</span>
                <p className="text-gray-800 mt-1">
                  {technical?.table_detection?.tables_found || 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Encrypted:</span>
                <p className="text-gray-800 mt-1">
                  {technical?.security?.encrypted ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Scanned Only:</span>
                <p className="text-gray-800 mt-1">
                  {technical?.scanned_only?.scanned_image_only_pdf
                    ? "Yes"
                    : "No"}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">
                  Digital Signature:
                </span>
                <p className="text-gray-800 mt-1">
                  {technical?.digital_signature?.digital_signature_present
                    ? "Yes"
                    : "No"}
                </p>
              </div>
            </div>

            {/* Enhanced Technical Analysis */}
            {technical?.obfuscated_text?.obfuscated_text_detected && (
              <div className="mt-3 p-2 bg-red-50 rounded">
                <p className="text-xs font-medium text-red-600">
                  🚨 Obfuscated Text Detected
                </p>
                <div className="text-xs text-gray-600 mt-1">
                  <div>
                    HTML chars: {technical.obfuscated_text.html_char_count}
                  </div>
                  <div>
                    Normal text chars:{" "}
                    {technical.obfuscated_text.normal_text_chars}
                  </div>
                  <div>
                    Raw dict blocks: {technical.obfuscated_text.raw_dict_blocks}
                  </div>
                </div>
              </div>
            )}

            {technical?.image_only_analysis && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Image Analysis:
                </p>
                <div className="space-y-1 text-xs">
                  <div>
                    Is image only:{" "}
                    {technical.image_only_analysis.is_image_only ? "Yes" : "No"}
                  </div>
                  <div>
                    Base64 images:{" "}
                    {technical.image_only_analysis.base64_image_count}
                  </div>
                  {technical.image_only_analysis.extraction_failure_reason && (
                    <div className="text-orange-600">
                      ⚠{" "}
                      {technical.image_only_analysis.extraction_failure_reason}
                    </div>
                  )}
                </div>
              </div>
            )}

            {technical?.raw_analysis && (
              <div className="mt-3 p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  Raw PDF Analysis:
                </p>
                <div className="space-y-1 text-xs">
                  <div>Objects: {technical.raw_analysis.object_count}</div>
                  <div>Streams: {technical.raw_analysis.stream_count}</div>
                  <div>XREF tables: {technical.raw_analysis.xref_tables}</div>
                </div>
              </div>
            )}

            {technical?.incremental_updates?.incremental_updates_detected && (
              <div className="mt-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs font-medium text-orange-600">
                  ⚠ Incremental updates detected
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  XREF: {technical.incremental_updates.xref_count}, EOF:{" "}
                  {technical.incremental_updates.eof_count}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
